<template>
  <div class="home">
    <div class="hero-section" :class="{ 'start-animations': startAnimations }">
      <div class="hero-badge" :class="{ 'start-animations': startAnimations }">
        🏆 World's Leading Digital Currency Identifier
      </div>
      <h1 class="title" :class="{ 'start-animations': startAnimations }">Go Coin Identifier</h1>
      <p class="subtitle" :class="{ 'start-animations': startAnimations }">
        Fast, Secure, and Convenient Cryptocurrency Identification Tool<br>
        AI-Powered, Accurately Identifying 1000+ Global Digital Currencies<br>
        <span class="highlight">98%</span> User Satisfaction · <span class="highlight">500K+</span> Downloads
      </p>
      <div class="hero-features" :class="{ 'start-animations': startAnimations }">
        <div class="hero-feature">
          <span class="hero-feature-icon">🔍</span>
          Instant Recognition
        </div>
        <div class="hero-feature">
          <span class="hero-feature-icon">🛡️</span>
          Secure & Reliable
        </div>
        <div class="hero-feature">
          <span class="hero-feature-icon">🌍</span>
          Multi-Language Support
        </div>
      </div>
      <div class="download-buttons" :class="{ 'start-animations': startAnimations }">
        <el-button 
          type="primary" 
          size="large" 
          class="download-btn ios-btn"
          @click="handleDownload('ios')"
        >
          <el-icon class="el-icon--left"><Apple /></el-icon>
          Download for iOS
        </el-button>
        <el-button 
          type="success" 
          size="large" 
          class="download-btn android-btn"
          @click="handleDownload('android')"
        >
          <el-icon class="el-icon--left"><Cellphone /></el-icon>
          Download for Android
        </el-button>
      </div>
    </div>

    <div class="features-section" :class="{ 'start-animations': startAnimations }">
      <h2 class="section-title">Features</h2>
      <el-row :gutter="20">
        <el-col :xs="24" :sm="8" :md="6">
          <div class="feature-card">
            <div class="feature-icon">⚡</div>
            <h3>Fast Recognition</h3>
            <p>Millisecond-level response, instant cryptocurrency information</p>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8" :md="6">
          <div class="feature-card">
            <div class="feature-icon">🔒</div>
            <h3>Secure & Reliable</h3>
            <p>Advanced encryption technology to protect your data security</p>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8" :md="6">
          <div class="feature-card">
            <div class="feature-icon">📱</div>
            <h3>Cross-Platform</h3>
            <p>Support for both iOS and Android platforms</p>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8" :md="6">
          <div class="feature-card">
            <div class="feature-icon">🌐</div>
            <h3>Global Support</h3>
            <p>Supporting multiple languages and regions worldwide</p>
          </div>
        </el-col>
      </el-row>
    </div>

    <div class="stats-section" :class="{ 'start-animations': startAnimations }">
      <h2 class="section-title">App Statistics</h2>
      <el-row :gutter="20">
        <el-col :xs="12" :sm="6" v-for="(stat, index) in stats" :key="index">
          <div class="stat-card">
            <div class="stat-number">{{ stat.number }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
        </el-col>
      </el-row>
    </div>

    <div class="how-it-works-section" :class="{ 'start-animations': startAnimations }">
      <h2 class="section-title">How It Works</h2>
      <el-row :gutter="30">
        <el-col :xs="24" :sm="8">
          <div class="step-card">
            <div class="step-number">1</div>
            <h3>Take a Photo</h3>
            <p>Simply take a clear photo of your cryptocurrency using your phone's camera</p>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8">
          <div class="step-card">
            <div class="step-number">2</div>
            <h3>AI Analysis</h3>
            <p>Our advanced AI system analyzes the image and identifies the cryptocurrency</p>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8">
          <div class="step-card">
            <div class="step-number">3</div>
            <h3>Get Results</h3>
            <p>Receive detailed information about the cryptocurrency, including current market value</p>
          </div>
        </el-col>
      </el-row>
    </div>

    <div class="testimonials-section" :class="{ 'start-animations': startAnimations }">
      <h2 class="section-title">What Users Say</h2>
      <el-row :gutter="30">
        <el-col :xs="24" :sm="8" v-for="(testimonial, index) in testimonials" :key="index">
          <div class="testimonial-card">
            <div class="testimonial-content">{{ testimonial.content }}</div>
            <div class="testimonial-author">{{ testimonial.author }}</div>
            <div class="testimonial-rating">★★★★★</div>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { Apple, Cellphone } from '@element-plus/icons-vue'

export default {
  name: 'HomeView',
  components: {
    Apple,
    Cellphone
  },
  setup() {
    const startAnimations = ref(false)
    
    const startPageAnimations = () => {
      startAnimations.value = true
    }
    
    onMounted(() => {
      window.addEventListener('loading-complete', startPageAnimations)
    })
    
    onBeforeUnmount(() => {
      window.removeEventListener('loading-complete', startPageAnimations)
    })
    
    const downloadLinks = {
      ios: 'https://apps.apple.com/us/app/go-coin-identifier-coin-value/id6739617412',
      android: 'https://play.google.com/store/apps/details?id=com.collect.coin.identifier'
    }
    
    const stats = [
      {
        number: '1000+',
        label: 'Supported Coins'
      },
      {
        number: '500K+',
        label: 'Downloads'
      },
      {
        number: '99.9%',
        label: 'Recognition Accuracy'
      },
      {
        number: '24/7',
        label: 'Service Availability'
      }
    ]
    
    const testimonials = [
      {
        content: "This app is amazing! It helped me identify my entire cryptocurrency collection in minutes.",
        author: "John D."
      },
      {
        content: "The accuracy is incredible. I've never seen anything like it.",
        author: "Sarah M."
      },
      {
        content: "Fast, reliable and user-friendly. Exactly what I needed!",
        author: "Mike R."
      }
    ]
    
    const handleDownload = (platform) => {
      const url = platform === 'ios' ? downloadLinks.ios : downloadLinks.android
      window.open(url, '_blank')
    }
    
    return {
      startAnimations,
      downloadLinks,
      stats,
      testimonials,
      handleDownload
    }
  }
}
</script>

<style>
.home {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a2a6c 0%, #b21f1f 50%, #fdbb2d 100%);
  padding: 20px;
  position: relative;
  z-index: 1;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.hero-section {
  text-align: center;
  padding: 120px 20px 80px;
  margin-bottom: 60px;
  position: relative;
  z-index: 2;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  animation: none;
}

.hero-section.start-animations {
  animation: fadeInUp 0.4s ease forwards;
  animation-delay: 0.1s;
}

.hero-badge {
  background: linear-gradient(45deg, #FFD700, #FFA500);
  color: #1a1a1a;
  padding: 8px 20px;
  border-radius: 20px;
  font-weight: bold;
  font-size: 1.1em;
  margin-bottom: 30px;
  opacity: 0;
  animation: none;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
}

.hero-badge.start-animations {
  animation: fadeInDown 0.4s ease forwards;
  animation-delay: 0.1s;
}

.highlight {
  color: #FFD700;
  font-weight: bold;
}

.hero-features {
  display: flex;
  gap: 30px;
  justify-content: center;
  margin: 40px 0;
  opacity: 0;
  animation: none;
}

.hero-features.start-animations {
  animation: fadeInUp 0.4s ease forwards;
  animation-delay: 0.35s;
}

.hero-feature {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1em;
  background: rgba(255, 255, 255, 0.1);
  padding: 10px 20px;
  border-radius: 15px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.hero-feature:hover {
  transform: translateY(-3px);
  background: rgba(255, 255, 255, 0.2);
}

.hero-feature-icon {
  font-size: 1.2em;
}

.title {
  font-size: 4em;
  color: white;
  margin-bottom: 30px;
  text-shadow: 0 2px 10px rgba(0,0,0,0.3);
  position: relative;
  z-index: 2;
  opacity: 0;
  animation: none;
}

.title.start-animations {
  animation: fadeInDown 0.4s ease forwards;
  animation-delay: 0.2s;
}

.subtitle {
  font-size: 1.4em;
  color: rgba(255,255,255,0.9);
  margin-bottom: 50px;
  line-height: 1.8;
  position: relative;
  z-index: 2;
  opacity: 0;
  animation: none;
}

.subtitle.start-animations {
  animation: fadeInUp 0.4s ease forwards;
  animation-delay: 0.3s;
}

.download-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-bottom: 50px;
  position: relative;
  z-index: 2;
  opacity: 0;
  animation: none;
}

.download-buttons.start-animations {
  animation: fadeIn 0.4s ease forwards;
  animation-delay: 0.4s;
}

.download-btn {
  padding: 15px 30px !important;
  font-size: 1.2em !important;
  border-radius: 50px !important;
  transition: all 0.3s ease !important;
  min-width: 220px !important;
  position: relative !important;
  overflow: hidden !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 10px !important;
}

.download-btn:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0,0,0,0.2);
}

.ios-btn {
  background: linear-gradient(45deg, #000000, #434343) !important;
  border: none !important;
  color: white !important;
}

.android-btn {
  background: linear-gradient(45deg, #11998e, #38ef7d) !important;
  border: none !important;
  color: white !important;
}

.section-title {
  font-size: 2.5rem;
  color: white;
  text-align: center;
  margin-bottom: 50px;
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg, #fdbb2d, #b21f1f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.features-section,
.stats-section,
.how-it-works-section,
.testimonials-section {
  background: rgba(255,255,255,0.1);
  border-radius: 20px;
  padding: 60px 20px;
  margin-bottom: 60px;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255,255,255,0.2);
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0;
  animation: none;
}

.el-row {
  margin-bottom: -30px !important;
  margin-right: -15px !important;
  margin-left: -15px !important;
}

.el-col {
  padding-bottom: 30px !important;
  padding-right: 15px !important;
  padding-left: 15px !important;
}

.features-section.start-animations {
  animation: fadeInUp 0.4s ease forwards;
  animation-delay: 0.3s;
}

.stats-section.start-animations { animation: fadeInUp 0.4s ease forwards; animation-delay: 0.6s; }
.how-it-works-section.start-animations { animation: fadeInUp 0.4s ease forwards; animation-delay: 0.7s; }
.testimonials-section.start-animations { animation: fadeInUp 0.4s ease forwards; animation-delay: 0.8s; }

.feature-card,
.step-card,
.testimonial-card,
.stat-card {
  background: rgba(255,255,255,0.15);
  border-radius: 15px;
  padding: 30px;
  text-align: center;
  transition: all 0.3s ease;
  height: 100%;
  border: 1px solid rgba(255,255,255,0.2);
  opacity: 0;
  animation: none;
  margin: 0;
}

.feature-card:hover,
.step-card:hover,
.testimonial-card:hover {
  transform: translateY(-10px);
  background: rgba(255,255,255,0.2);
}

.start-animations .feature-card:nth-child(1) { animation: fadeInUp 0.3s ease forwards; animation-delay: 0.9s; }
.start-animations .feature-card:nth-child(2) { animation: fadeInUp 0.3s ease forwards; animation-delay: 1.0s; }
.start-animations .feature-card:nth-child(3) { animation: fadeInUp 0.3s ease forwards; animation-delay: 1.1s; }
.start-animations .feature-card:nth-child(4) { animation: fadeInUp 0.3s ease forwards; animation-delay: 1.2s; }

.start-animations .step-card:nth-child(1) { animation: fadeInUp 0.3s ease forwards; animation-delay: 1.3s; }
.start-animations .step-card:nth-child(2) { animation: fadeInUp 0.3s ease forwards; animation-delay: 1.4s; }
.start-animations .step-card:nth-child(3) { animation: fadeInUp 0.3s ease forwards; animation-delay: 1.5s; }

.start-animations .testimonial-card:nth-child(1) { animation: fadeInUp 0.3s ease forwards; animation-delay: 1.6s; }
.start-animations .testimonial-card:nth-child(2) { animation: fadeInUp 0.3s ease forwards; animation-delay: 1.7s; }
.start-animations .testimonial-card:nth-child(3) { animation: fadeInUp 0.3s ease forwards; animation-delay: 1.8s; }

.start-animations .stat-card:nth-child(1) { animation: fadeInUp 0.3s ease forwards; animation-delay: 0.9s; }
.start-animations .stat-card:nth-child(2) { animation: fadeInUp 0.3s ease forwards; animation-delay: 1.0s; }
.start-animations .stat-card:nth-child(3) { animation: fadeInUp 0.3s ease forwards; animation-delay: 1.1s; }
.start-animations .stat-card:nth-child(4) { animation: fadeInUp 0.3s ease forwards; animation-delay: 1.2s; }

.step-number {
  width: 40px;
  height: 40px;
  background: linear-gradient(45deg, #fdbb2d, #b21f1f);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5em;
  color: white;
  margin: 0 auto 20px;
  font-weight: bold;
}

.testimonial-content {
  font-style: italic;
  color: rgba(255,255,255,0.9);
  margin-bottom: 20px;
  line-height: 1.6;
}

.testimonial-author {
  color: #fdbb2d;
  font-weight: 500;
  margin-bottom: 10px;
}

.testimonial-rating {
  color: #fdbb2d;
  font-size: 1.2em;
}

.feature-icon {
  font-size: 3rem;
  margin-bottom: 20px;
}

.feature-card h3 {
  font-size: 1.5rem;
  margin-bottom: 15px;
  color: #fdbb2d;
}

.feature-card p {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin: 0;
}

.stat-card {
  background: rgba(255,255,255,0.15);
  border-radius: 20px;
  padding: 40px 20px;
  text-align: center;
  transition: all 0.3s ease;
  height: 100%;
  border: 1px solid rgba(255,255,255,0.2);
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 15px;
  opacity: 0;
  animation: none;
}

.stat-card:hover {
  transform: translateY(-5px);
  background: rgba(255,255,255,0.2);
  border-color: rgba(255,255,255,0.3);
  box-shadow: 0 15px 35px rgba(0,0,0,0.2);
}

.stat-number {
  font-size: 3em;
  font-weight: bold;
  background: linear-gradient(135deg, #fdbb2d, #b21f1f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin: 0;
}

.stat-label {
  color: rgba(255,255,255,0.9);
  font-size: 1.2em;
  font-weight: 500;
  line-height: 1.4;
  margin: 0;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding: 60px 20px;
    min-height: auto;
  }

  .title {
    font-size: 2.5em;
  }

  .subtitle {
    font-size: 1.2em;
  }

  .download-buttons {
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 15px;
  }

  .download-btn {
    width: 100% !important;
    max-width: 300px !important;
  }

  .features-section,
  .stats-section,
  .how-it-works-section,
  .testimonials-section {
    padding: 40px 15px;
  }

  .el-row {
    margin-bottom: -20px !important;
  }

  .el-col {
    padding-bottom: 20px !important;
  }

  .section-title {
    font-size: 2rem;
    margin-bottom: 40px;
  }

  .feature-card {
    margin-bottom: 20px;
  }

  .hero-features {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }

  .hero-feature {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }

  .hero-badge {
    font-size: 1em;
    text-align: center;
  }

  .stat-card {
    padding: 30px 15px;
  }

  .stat-number {
    font-size: 2.5em;
  }

  .stat-label {
    font-size: 1.1em;
  }
}
</style> 